/**
 * Content Security Policy (CSP) Configuration
 *
 * Defines security policies to prevent XSS, injection attacks, and other code injection vulnerabilities
 *
 * CSP directives explained:
 * - default-src: Fallback for other fetch directives
 * - script-src: Controls which scripts can be executed
 * - style-src: Controls which stylesheets can be applied
 * - img-src: Controls which images can be loaded
 * - font-src: Controls which fonts can be loaded
 * - connect-src: Controls which URLs can be loaded using script interfaces
 * - media-src: Controls which video/audio sources can be loaded
 * - frame-src: Controls which frames can be embedded
 * - object-src: Controls <object>, <embed>, and <applet> elements
 */

export const CSP_CONFIG = {
    // Allow self-hosted resources as default
    'default-src': [
        "'self'"
    ],

    // Scripts: Allow self + inline scripts (needed for Vue) + Google Fonts preconnect
    // In production, consider using nonces or hashes instead of 'unsafe-inline'
    'script-src': [
        "'self'",
        "'unsafe-inline'", // Required for Vue inline event handlers and GSAP animations
        "'unsafe-eval'",   // Required for Vue development mode (remove in production if possible)
        'https://fonts.googleapis.com',
        'https://fonts.gstatic.com',
        'https://tracking.agency-19.com', // Matomo analytics
    ],

    // Styles: Allow self + inline styles (Vue scoped styles) + Google Fonts
    'style-src': [
        "'self'",
        "'unsafe-inline'", // Required for Vue scoped styles and dynamic GSAP animations
        'https://fonts.googleapis.com',
        'https://fonts.gstatic.com'
    ],

    // Images: Allow self + data URIs + blob for canvas/dynamic images
    'img-src': [
        "'self'",
        'data:',
        'blob:',
        'https://tracking.agency-19.com', // Matomo analytics tracking pixel
        'https:' // Allow HTTPS images (can be restricted to specific domains)
    ],

    // Fonts: Allow self + Google Fonts + data URIs
    'font-src': [
        "'self'",
        'data:',
        'https://fonts.googleapis.com',
        'https://fonts.gstatic.com'
    ],

    // Connect: API calls and fetch requests
    'connect-src': [
        "'self'",
        'ws:', // WebSocket for development HMR
        'wss:', // Secure WebSocket
        'https://tracking.agency-19.com', // Matomo analytics
    ],

    // Media: Video and audio sources (allowing self-hosted media)
    'media-src': [
        "'self'",
        'blob:',
        'data:'
    ],

    // Frames: Control iframe embedding (currently restrictive)
    'frame-src': [
        "'self'"
    ],

    // Objects: Disable plugins like Flash
    'object-src': [
        "'none'"
    ],

    // Base URI: Restrict base tag
    'base-uri': [
        "'self'"
    ],

    // Form actions: Restrict where forms can submit
    'form-action': [
        "'self'"
    ],

    // Frame ancestors: Prevent clickjacking (don't allow site to be framed)
    // NOTE: This directive only works via HTTP headers, not <meta> tags
    // Set this in your server configuration (nginx, Apache, etc.)
    'frame-ancestors': [
        "'none'"
    ],

    // Upgrade insecure requests: Automatically upgrade HTTP to HTTPS
    'upgrade-insecure-requests': true
};

/**
 * Generate CSP string from configuration object
 * @param {Object} config - CSP configuration object
 * @returns {string} CSP header/meta tag content
 */
export function generateCSPString(config = CSP_CONFIG) {
    const directives = [];

    Object.entries(config).forEach(([directive, value]) => {
        if (typeof value === 'boolean') {
            // For boolean directives like upgrade-insecure-requests
            if (value) {
                directives.push(directive);
            }
        } else if (Array.isArray(value) && value.length > 0) {
            // For array directives
            directives.push(`${directive} ${value.join(' ')}`);
        } else if (typeof value === 'string') {
            // For string directives
            directives.push(`${directive} ${value}`);
        }
    });

    return directives.join('; ');
}

/**
 * Production CSP Configuration (more restrictive)
 * Remove 'unsafe-eval' and potentially 'unsafe-inline' in production
 * Use nonces or hashes for inline scripts/styles instead
 */
export const CSP_PRODUCTION_CONFIG = {
    ...CSP_CONFIG,
    'script-src': [
        "'self'",
        "'unsafe-inline'", // Still needed for Vue SPA, but consider using nonces
        'https://fonts.googleapis.com',
        'https://fonts.gstatic.com',
        'https://tracking.agency-19.com', // Matomo analytics
    ],
};

/**
 * Development CSP Configuration (more permissive for HMR)
 */
export const CSP_DEVELOPMENT_CONFIG = {
    ...CSP_CONFIG,
    'script-src': [
        "'self'",
        "'unsafe-inline'",
        "'unsafe-eval'", // Required for Vue dev mode and HMR
        'https://fonts.googleapis.com',
        'https://fonts.gstatic.com',
    ],
    'connect-src': [
        "'self'",
        'ws:', // Allow WebSocket for HMR (Hot Module Replacement)
        'wss:',
    ],
};

/**
 * Get appropriate CSP config based on environment
 */
export function getCSPConfig() {
    const isDevelopment = process.env.NODE_ENV === 'development';
    return isDevelopment ? CSP_DEVELOPMENT_CONFIG : CSP_PRODUCTION_CONFIG;
}

export default {
    CSP_CONFIG,
    CSP_PRODUCTION_CONFIG,
    CSP_DEVELOPMENT_CONFIG,
    generateCSPString,
    getCSPConfig
};
